import { useCallback, useEffect, useRef } from "react";
import { z } from "zod";
import {
  SCROLLPORT_CONTAINER_CLASS,
  Box,
  ScrollArea,
  Skeleton,
  Text,
  WidgetEmpty,
  WidgetScrollEndGutter,
  WidgetSkeleton,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { BaseItem } from "#app/components/issue-view/activity/base-item";
import { EntityTag } from "#app/components/entity";
import { useIssueView } from "#app/components/issue-view";
import { useActivityList, type ActivityFeedRow } from "./use-activity-list";

/**
 * `activity-list` — the Add-card "Activity" feed: the scoped workspace activity
 * timeline rendered through the SAME `BaseItem` row the issue view uses, so every
 * actor/stage/assignee glyph routes through `#app/components/entity` and reads exactly
 * like a project's own history — never a hand-rolled feed. Each row carries its
 * parent-issue chip and opens the issue view on press; rows that reference a note
 * land on the activity tab focused on that note, the same deep link the inbox
 * uses. Reads `useActivityList` (the activity-shaped scope fold), which lazily
 * pages the WHOLE scoped feed — scrolling near the end appends the next server
 * page, so every entry is reachable and nothing is a capped chunk. Team
 * dashboards narrow to that team's actions; a workflow board folds its pinned
 * entity through the issue side (the activity schema has no `workflowId`).
 *
 * `feed` picks what the card is about. `actions` is the scoped feed as the
 * dashboard frames it. `items` is what OTHERS did on the projects assigned to
 * the viewer — My Work's "Activity on my work" card, where a mention, a
 * comment and a stage move are all the same thing: activity on something you
 * hold. One card, one setting; never a second feed module.
 */
export const activityListConfigSchema = z.looseObject({
  feed: z.enum(["actions", "items"]).default("actions"),
});

export type ActivityListConfig = z.infer<typeof activityListConfigSchema>;

/** The note a pressed row lands on: a created/edited note, a visit note, or
 *  the reply a noteReply changeset references. Deleted notes have no target. */
function resolveFocusNoteId(activity: ActivityFeedRow): string | null {
  const { note, age, noteReply } = activity.changeset ?? {};
  if (note?.after) return note.after as string;
  if (age?.after?.noteId) return age.after.noteId as string;
  return Object.keys(noteReply ?? {})[0] ?? null;
}

function ActivityListRender({ config }: WidgetRenderContext<ActivityListConfig>) {
  const { rows, loading, loadingMore, hasMore, loadMore } = useActivityList(config.feed);
  const { openIssueById } = useIssueView();

  const handlePress = useCallback(
    (activity: ActivityFeedRow) => {
      const focusNoteId = resolveFocusNoteId(activity);
      openIssueById(
        activity.issueId,
        focusNoteId
          ? { initialTab: "activity", focusNoteId, source: "widget" }
          : { source: "widget" },
      );
    },
    [openIssueById],
  );

  // Lazy paging: a sentinel after the last row appends the next server page
  // as it scrolls into view (`loadMore` self-guards against re-fires and
  // exhaustion) — the pattern the other row lists page by, so the feed can
  // sit in the library ScrollArea and wear its edge fade.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    if (!hasMore || loadingMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting) loadMore();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMore]);

  if (loading) {
    // Anonymous sentence rows on the feed's own inset: the body is flush, so
    // the placeholder restores the padding the settled scroller does.
    return (
      <Box fill="flex" paddingX="sm" paddingTop="base">
        <WidgetSkeleton variant="activity" />
      </Box>
    );
  }
  if (rows.length === 0) {
    return (
      <WidgetEmpty
        title={config.feed === "items" ? "No changes on your work" : "No recent activity"}
      />
    );
  }

  return (
    // The library scroller, faded at its edges while rows continue past
    // them; the sentinel below pages, so nothing reads scroll offsets.
    <ScrollArea fill scrollFade className={SCROLLPORT_CONTAINER_CLASS}>
      {/* `body: "flush"` drops the kit's insets, so the feed restores them: the
          same `base` under the header every grid body keeps, `sm` at the
          sides, and the scroll-end slack every widget scrollport ends on
          (the gutter, last in this column; the root is its size container). */}
      <Box paddingX="sm" paddingTop="base">
        {rows.map((activity, index) => (
          <Box
            key={`${activity.activityId}-${index}`}
            as="button"
            type="button"
            direction="row"
            align="center"
            gap="sm"
            group="row"
            paddingX="xs"
            marginBottom="md"
            // A button sizes to its content even as a flex container, and the
            // one-line sentence is as wide as its words: without the full
            // width the row grows past the card and the ellipsis never fires.
            fill="width"
            minWidth={0}
            onClick={() => handlePress(activity)}
          >
            <Box flex={1} minWidth={0}>
              {/* The SAME row the issue timeline draws — glyph lane, sentence,
                  " · <time>" — so the card reads like a project's history.
                  ONE line per row: the sentence names the project inline
                  (" on <chip>") and truncates; the time trails the sentence
                  and never falls to the ellipsis. Relative reading, since the
                  rows span days with no separators. The chip is inert — the
                  row owns navigation. */}
              <BaseItem
                activity={activity}
                issueId={activity.issueId}
                last={index === rows.length - 1}
                bottomSpacing={false}
                truncate
                timeReading="relative"
                trailing={
                  activity.issue ? (
                    <Text size="sm">
                      {" on "}
                      {/* The name is where the row goes: it carries the
                          actor's weight at rest and, like the actor, steps
                          up to body ink when the row is hovered — the verb
                          between them keeps the sentence's muted rung. */}
                      <Text size="sm" hoverInk="row">
                        <EntityTag
                          entity={{
                            kind: "issue",
                            id: activity.issue.issueId,
                            issue: activity.issue,
                          }}
                          display="label"
                          as="text"
                          size="sm"
                          weight="semibold"
                          // The row owns the press; the name still owns its
                          // hover: a lit word that disclosed nothing would
                          // be a broken promise, and the card also restores
                          // a truncated name.
                          preview
                          interactive={false}
                        />
                      </Text>
                    </Text>
                  ) : null
                }
              />
            </Box>
          </Box>
        ))}
        {loadingMore ? (
          <Box paddingX="xs" paddingY="sm">
            <Skeleton height={16} />
          </Box>
        ) : null}
        {hasMore ? <Box ref={sentinelRef} height={1} shrink={false} /> : null}
        <WidgetScrollEndGutter />
      </Box>
    </ScrollArea>
  );
}

const activityListSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "feed",
        kind: "select",
        label: "Show",
        icon: "filter",
        options: [
          { value: "actions", label: "All activity" },
          { value: "items", label: "On my work" },
        ],
      },
    ],
  },
];

export const activityListDef: WidgetModule<ActivityListConfig> = {
  type: "activity-list",
  title: "Activity",
  icon: "activity",
  // The feed owns its scroll viewport: let its scrollbar meet the card edge and
  // header divider while the inner Box keeps the activity rows comfortably inset.
  body: "flush",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 4 },
  configSchema: activityListConfigSchema,
  settings: activityListSettings,
  catalog: [
    {
      key: "activity-list.default",
      label: "Activity",
      description: "A live feed of the latest actions and Comments across the Projects in scope.",
      section: "activity" satisfies WidgetSection,
      tags: ["projects", "activity"] satisfies readonly WidgetTag[],
      defaultConfig: { feed: "actions" },
      defaultW: 4,
    },
  ],
  render: ActivityListRender,
};
